import { ActionTypes, DialogTypes, ContentTypes } from "../constants/action-types";
import { fromJS } from "immutable";
import { handleActions } from "redux-actions";

const groupByDefault = "bucketId";

export const defaultTask = {
  data: {
    name: {
      value: "",
    },
    bucketId: {
      value: "",
    },
    start: {
      value: new Date(),
    },
  },
};

export const initialState = fromJS({
  tasks: [],
  pending: true,
  task: {
    [ContentTypes.SALE]: defaultTask,
    [ContentTypes.CONTACT]: defaultTask,
    [ContentTypes.COMPANY]: defaultTask,
    [ContentTypes.PRODUCT]: defaultTask,
  },
  filters: {},
  sorts: [],
  groupBy: groupByDefault,
  [groupByDefault]: [],
  groupByVisibleFieldIds: [],
  boardCardFields: [],
  gridFields: [],
  showCardLabels: false,
  showViewModeDialog: {
    show: false,
    myFilter: {
      name: "",
      visibility: "Private",
      data: {},
    },
  },
  allAssignees: [],
  showRightPanelDialog: {
    show: false,
    type: DialogTypes.mainDialog,
    data: {},
  },
  showTaskDialog: {
    [ContentTypes.SALE]: {
      show: false,
      task: {},
    },
    [ContentTypes.CONTACT]: {
      show: false,
      task: {},
    },
    [ContentTypes.COMPANY]: {
      show: false,
      task: {},
    },
    [ContentTypes.PRODUCT]: {
      show: false,
      task: {},
    },
  },
  GRID: {
    children: [],
  },
  CALENDAR: {
    children: [],
  },
  BOARD: {
    children: [],
  },
  CHART: {
    children: [],
  },
  MAP: {
    children: [],
  },
  pagination: {
    first: true,
    number: 0,
    size: 50,
  },
  hasFilter: false,
  selectionIds: [],
});

const doGroupBy = (tasks, groupById) => {
  return tasks.groupBy((task) => task.getIn(["data", groupById, "value"])).toJS();
};

export const actions = {
  [ActionTypes.getItem]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const newState = {
        ...state.toJS(),
        pending: false,
        task: {
          ...state.get("task").toJS(),
          [payload.type]: payload,
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.updateTask]: {
    next(state, { payload }) {
      if (payload == null) {
        return state;
      }
      const { taskId, view, item } = payload;
      const tasks = state.getIn([view, "children"]) || state.get("tasks");
      // Update task list
      const taskIndex = tasks.findIndex((item) => item.get("id") === taskId);
      const newTaskList = tasks.setIn([taskIndex], item).toJS();
      return fromJS({
        ...state.toJS(),
        [view]: {
          children: newTaskList,
        },
        tasks: newTaskList,
        task: {
          ...state.get("task").toJS(),
          [item.type]: item,
        },
      });
    },
  },
  [ActionTypes.resetTask]: {
    next(state, { payload }) {
      if (payload == null) {
        return state;
      }
      const { itemType } = payload;
      return fromJS({
        ...state.toJS(),
        task: {
          ...state.get("task").toJS(),
          [itemType]: defaultTask,
        },
      });
    },
  },
  [ActionTypes.searchItems]: {
    next(state, { payload, error, pending }) {
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const { data, view } = payload;
      const { pageResult, hasFilter, sorts = [] } = data;
      const { content = [] } = pageResult;
      const items = content;
      const newState = {
        ...state.toJS(),
        [view]: {
          children: items,
        },
        tasks: items,
        pending: false,
        pagination: {
          ...pageResult,
          content: [],
        },
        hasFilter,
        sorts,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.selectItem]: {
    next(state, { payload }) {
      const { itemId } = payload;
      const selectionIds = state.get("selectionIds");
      if (!selectionIds.includes(itemId)) {
        const newList = selectionIds.push(itemId);
        return state.set("selectionIds", newList);
      }
      const index = selectionIds.indexOf(itemId);
      const newList = selectionIds.delete(index);
      return state.set("selectionIds", newList);
    },
  },
  [ActionTypes.selectAllItems]: {
    next(state, { payload }) {
      const { view, selected } = payload;
      const selectionIds = state.get("selectionIds");
      if (!selected) {
        return state.set("selectionIds", selectionIds.clear());
      }
      const itemIds = state.getIn([view, "children"]).map((item) => item.get("id"));
      return state.set("selectionIds", itemIds);
    },
  },
  [ActionTypes.changeFilters]: {
    next(state, { payload }) {
      const { filters } = payload;
      const newState = { ...state.toJS(), filters };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeSorts]: {
    next(state, { payload }) {
      const { sorts } = payload;
      const newState = { ...state.toJS(), sorts };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeGroupBy]: {
    next(state, { payload }) {
      const { groupId, groupColumnIds } = payload;
      const groups = doGroupBy(state.get("tasks"), groupId);
      const newState = {
        ...state.toJS(),
        groupBy: groupId,
        groupByVisibleFieldIds: groupColumnIds,
        [groupId]: groups,
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeBoardCardFields]: {
    next(state, { payload }) {
      return state.set("boardCardFields", fromJS(payload));
    },
  },
  [ActionTypes.changeGridFields]: {
    next(state, { payload }) {
      return state.set("gridFields", fromJS(payload));
    },
  },
  [ActionTypes.changeShowCardLabels]: {
    next(state, { payload }) {
      return state.set("showCardLabels", payload);
    },
  },
  [ActionTypes.changeViewModeData]: {
    next(state, { payload }) {
      const { fieldId, fieldValue } = payload;
      const newState = {
        ...state.toJS(),
        showViewModeDialog: {
          ...state.get("showViewModeDialog").toJS(),
          myFilter: {
            ...state.getIn(["showViewModeDialog", "myFilter"]).toJS(),
            [fieldId]: fieldValue,
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.showViewModeDialog]: {
    next(state, data) {
      const { payload, error, pending } = data;
      if (error) {
        return state.merge({ error, pending });
      }
      if (pending) {
        return state.merge({ pending });
      }
      if (payload === null) {
        return state;
      }
      const viewModeData = fromJS(payload || {});
      const newState = {
        ...state.toJS(),
        showViewModeDialog: {
          show: true,
          myFilter: {
            ...viewModeData.toJS(),
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideViewModeDialog]: {
    next(state) {
      const newState = {
        ...state.toJS(),
        showViewModeDialog: {
          show: false,
          myFilter: {
            name: "",
            visibility: "Private",
            data: {},
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.changeFilterString]: {
    next(state, { payload }) {
      const { filterString, searchMode } = payload;
      const newState = { ...state.toJS(), filterString, searchMode };
      return fromJS(newState);
    },
  },
  [ActionTypes.showRightPanelDialog]: {
    next(state, { payload }) {
      const { data, dialogType } = payload;
      const newState = {
        ...state.toJS(),
        showRightPanelDialog: {
          show: true,
          type: dialogType,
          data,
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideRightPanelDialog]: {
    next(state, { payload }) {
      const { dialogType } = payload;
      const newState = {
        ...state.toJS(),
        showRightPanelDialog: {
          show: false,
          type: dialogType,
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.showTaskDialog]: {
    next(state, { payload }) {
      const { task, contentType } = payload;
      const newState = {
        ...state.toJS(),
        showTaskDialog: {
          ...state.get("showTaskDialog").toJS(),
          [contentType]: {
            show: true,
            task,
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.hideTaskDialog]: {
    next(state, { payload }) {
      const { contentType } = payload;
      const newState = {
        ...state.toJS(),
        showTaskDialog: {
          ...state.get("showTaskDialog").toJS(),
          [contentType]: {
            show: false,
            task: {},
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.updateTaskDialog]: {
    next(state, { payload, pending }) {
      if (pending) {
        return state.merge({ pending });
      }
      const { contentType, fieldId, fieldValue } = payload;
      const newState = {
        ...state.toJS(),
        showTaskDialog: {
          ...state.get("showTaskDialog").toJS(),
          [contentType]: {
            ...state.getIn(["showTaskDialog", contentType]).toJS(),
            task: {
              ...state.getIn(["showTaskDialog", contentType, "task"]).toJS(),
              data: {
                ...(state.getIn(["showTaskDialog", contentType, "task", "data"])?.toJS() || {}),
                [fieldId]: {
                  ...(state
                    .getIn(["showTaskDialog", contentType, "task", "data", fieldId])
                    ?.toJS() || {}),
                  value: fieldValue,
                },
              },
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  [ActionTypes.resetTaskDialog]: {
    next(state, { payload }) {
      const { contentType, taskData } = payload;
      const newState = {
        ...state.toJS(),
        showTaskDialog: {
          ...state.get("showTaskDialog").toJS(),
          [contentType]: {
            ...state.getIn(["showTaskDialog", contentType]).toJS(),
            task: {
              ...state.getIn(["showTaskDialog", contentType, "task"]).toJS(),
              data: {
                ...taskData,
              },
            },
          },
        },
      };
      return fromJS(newState);
    },
  },
  // Synchronous action to merge interaction stats into an existing task (no API call)
  [ActionTypes.updateTaskData]: {
    next(state, { payload }) {
      const { taskId, item, view } = payload;
      const views = view ? [view] : ["BOARD"];
      let newState = state;
      views.forEach((v) => {
        const tasks = newState.getIn([v, "children"]);
        if (tasks) {
          const taskIndex = tasks.findIndex((t) => t.get("id") === taskId);
          if (taskIndex >= 0) {
            newState = newState.setIn([v, "children", taskIndex], fromJS(item));
          }
        }
      });
      // Also update the global tasks list
      const globalTasks = newState.get("tasks");
      if (globalTasks) {
        const gIdx = globalTasks.findIndex((t) => t.get("id") === taskId);
        if (gIdx >= 0) {
          newState = newState.setIn(["tasks", gIdx], fromJS(item));
        }
      }
      return newState;
    },
  },
};

export const taskReducer = handleActions(actions, initialState);
